import { useEffect, useRef } from 'react';

interface WebSocketHookProps {
  onOpen?: () => void;
  onMessage?: (data: any) => void;
  onError?: (error: any) => void;
  onClose?: () => void;
}

interface WebSocketHookReturn {
  sendMessage: (message: any) => void;
  sendBinaryMessage: (blob: Blob) => void;
  wsRef: React.MutableRefObject<WebSocket | null>;
  initWebSocket: () => void;
}

export const useWebSocket = ({
  onOpen,
  onMessage,
  onError,
  onClose
}: WebSocketHookProps): WebSocketHookReturn => {
  const wsRef = useRef<WebSocket | null>(null);

  const initWebSocket = () => {
    // No iniciar si ya hay una conexión activa
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Limpiar conexión existente
    if (wsRef.current) {
      //wsRef.current.close();
      wsRef.current = null;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);
    
    ws.onopen = () => {
      onOpen?.();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // Validar el tipo de mensaje
        if (!data.type) {
          throw new Error('Message type not specified');
        }
        console.log('[WebSocket] Mensaje recibido:', data);
        // Tipos de mensajes esperados:
        // - initial_config: Configuración inicial (incluye avatar y token de HeyGen)
        // - generated_image: Imagen generada (formato base64 o url)
        // - response_complete: Respuesta del asistente completada
        // - error: Error en el proceso

        // Validar formato para mensajes de tipo generated_image
        if (data.type === 'generated_image') {
          if (data.format === 'base64' && !data.data) {
            throw new Error('Base64 image data missing');
          } else if (!data.format && !data.url) {
            throw new Error('Image URL missing');
          }
        }
        onMessage?.(data);
      } catch (error) {
        console.error('[WebSocket] Message parse error:', error);
        onError?.(error);
      }
    };

    ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
      onError?.(error);
    };

    ws.onclose = (event) => {
      console.log('[WebSocket] Conexión cerrada:', event.code, event.reason);
      wsRef.current = null;
      onClose?.();
    };

    wsRef.current = ws;
  };

  const sendMessage = (message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
      wsRef.current.send(messageStr);
    } else {
      console.warn('[WebSocket] Cannot send message: connection not open');
    }
  };

  const sendBinaryMessage = (blob: Blob) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(blob);
    } else {
      console.warn('[WebSocket] Cannot send binary message: connection not open');
    }
  };

  // Iniciar WebSocket al montar el componente
  useEffect(() => {
    initWebSocket();
    // Cleanup al desmontar
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  return {
    sendMessage,
    sendBinaryMessage,
    wsRef,
    initWebSocket
  };
};
